import { useEffect, useState } from 'react';
import { Authenticator, CheckboxField, SelectField, useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { isValidIBAN } from 'ibantools';
import { useTermsApiHook } from '../terms';
import getClientInfo from '../../clients';

import '@aws-amplify/ui-react/styles.css';
import './login-styles.scss';

import LifepowrLogo from '@lifepowr/components/src/assets/logos/LPLogo.png';

const formFields = {
  signIn: {
    username: {
      labelHidden: false,
      placeholder: 'Email',
      label: 'Email:',
    },
    password: {
      labelHidden: false,
      placeholder: 'Password',
      label: 'Password:',
    },
  },
  signUp: {
    username: {
      label: 'Email',
      placeholder: 'Email',
      isRequired: true,
      labelHidden: false,
    },
    password: {
      label: 'Password',
      placeholder: 'Password',
      isRequired: true,
      labelHidden: false,
    },
    'custom:firstName': {
      label: 'First Name',
      placeholder: 'First Name',
      isRequired: true,
      labelHidden: false,
    },
    'custom:lastName': {
      label: 'Last Name',
      placeholder: 'Last Name',
      isRequired: true,
      labelHidden: false,
    },
    'custom:iban': {
      label: 'IBAN',
      placeholder: 'eg: BEXXXXXXXXXXXXXX',
      isRequired: false,
      labelHidden: false,
    },
  },
  resetPassword: {
    username: {
      placeholder: 'Enter your email',
    },
  },
};

const services = {
  async handleSignUp(formData) {
    const regex = / /gi;
    let { username, password, attributes } = formData;
    // custom username
    username = username.replace(regex, '');
    username = username.toLowerCase();
    return Auth.signUp({
      username,
      password,
      attributes,
    });
  },
  async handleSignIn(formData) {
    const regex = / /gi;
    let { username, password, attributes } = formData;
    // custom username
    username = username.replace(regex, '');
    username = username.toLowerCase();
    return Auth.signIn({
      username,
      password,
      attributes,
    });
  },
  async handleForgotPassword(username) {
    const regex = / /gi;
    let newUsername = username.replace(regex, '');
    newUsername = newUsername.toLowerCase();
    return Auth.forgotPassword(newUsername);
  },
  async handleForgotPasswordSubmit(formData) {
    const regex = / /gi;
    let { username, code, password } = formData;
    username = username.replace(regex, '');
    username = username.toLowerCase();
    return Auth.forgotPasswordSubmit(username, code, password);
  },
  async validateCustomSignUp(formData) {
    if (!formData['custom:tocversion']) {
      return {
        'custom:terms': 'You must agree to the Terms & Conditions',
      };
    }
    if (formData['custom:iban'] && !isValidIBAN(formData['custom:iban'])) {
      return {
        'custom:iban': 'Invalid IBAN',
      };
    }
  },
};

const Header = () => {
  const clientInfo = getClientInfo();

  return (
    <div style={{ width: '100%', marginBottom: '20px', display: 'grid', justifyContent: 'center' }}>
      {clientInfo?.logo ? (
        <clientInfo.logo />
      ) : (
        <img
          src={LifepowrLogo}
          alt="lplogo"
        />
      )}
    </div>
  );
};

const components = {
  Header,

  SignUp: {
    FormFields: () => {
      const [open, setOpen] = useState(false);
      const authenticator = useAuthenticator();
      const { termsApiClientStatus, setUserName } = useTermsApiHook();

      const terms = termsApiClientStatus.data?.terms;
      const version = termsApiClientStatus.data?.version;
      const error = termsApiClientStatus.error;

      const strRes = error ? error : 'Loading . . .';

      setUserName('__UNREGISTERED__');

      const Label = () => {
        return (
          <>
            I accept Lifepowr's{' '}
            <div
              onClick={(ev) => {
                ev.preventDefault();
                setOpen(true);
              }}
              className="login-checkbox"
            >
              terms of use
            </div>
          </>
        );
      };

      return (
        <>
          {/* Re-use default `Authenticator.SignUp.FormFields` */}
          <Authenticator.SignUp.FormFields />

          {/* Append & require Terms & Conditions field to sign up  */}
          <SelectField
            name="custom:language"
            label="Language"
            isRequired
          >
            <option value="En">English</option>
            <option value="Nl">Nederlands</option>
          </SelectField>

          <CheckboxField
            hasError={'acknowledgement' in authenticator.validationErrors}
            name="custom:tocversion"
            label={<Label />}
            value={version}
            isRequired
          />

          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            scroll="paper"
          >
            <DialogTitle>Lifepowr Terms and Conditions</DialogTitle>

            <DialogContent
              style={{ paddingLeft: '35px' }}
              dangerouslySetInnerHTML={{ __html: terms ? terms : strRes }}
            />
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      );
    },
  },
};

const fedSignIn = async (idp) => {
  try {
    await Auth.federatedSignIn({ customProvider: idp });
  } catch (error) {
    console.log('error signing in', error);
  }
};

export function CheckLogin(props) {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [clientInfo] = useState(getClientInfo());

  useEffect(() => {
    if (clientInfo?.idp && authStatus === 'unauthenticated') {
      fedSignIn(clientInfo.idp);
    }
  }, [clientInfo, authStatus]);

  /*
  // This code is not validated, so we may need it for federated IdP
  window.document.cookie.split(';').forEach((c) => {
    const [k, v] = c.split('=');

    if (k?.includes('CognitoIdentityServiceProvider')) {
      localStorage.setItem(k.trim(), v.trim());
    }
  });
  */

  if (authStatus === 'authenticated') {
    return props.children;
  } else {
    const loginClass = clientInfo?.loginClass ?? 'loginForm';

    return (
      <Authenticator
        className={loginClass}
        services={services}
        formFields={formFields}
        hideSignUp={true}
        components={components}
      />
    );
  }
}
